'use client';

// The screen before there is a screen.
//
// Two separate waits used to be shown as one thing — nothing. On the server
// there is no localStorage, so `getServerSnapshot` in lib/token-store answers
// "no token", and the first paint of every reload was therefore the password
// gate. A browser that had been unlocked for weeks still got it, for as long as
// hydration took, and then it was replaced by an app with no name in the account
// row, a skeleton where the wiki goes and an empty board. The gate was not wrong
// so much as premature: "no token" and "nobody has looked yet" are different
// facts and only one of them is worth showing somebody.
//
// So this holds the door instead. It is the only thing rendered until the app
// knows which of the two it is looking at — see `ready` in ./console — and then
// it dissolves and whatever was behind it comes up through it. The mark is the
// whole of the content on purpose: there is nothing here anyone can act on, and
// a spinner would be an interface element saying "wait" where the brand can say
// the same thing and be worth looking at while it does.
//
// The dissolve is not decoration. Arriving is the one moment this app has to say
// what kind of thing it is, and a cut says nothing.

import { useEffect, useRef, useState } from 'react';

export type BootPhase = 'held' | 'leaving' | 'gone';

/* How long the mark stays even when there was nothing to wait for. A reload
   against a runner on this same machine answers /api/me in single figures of
   milliseconds, and with no floor the veil is a one-frame flash — which reads
   as a glitch rather than as a screen. Half a second is long enough to be a
   thing that happened and short enough that nobody is waiting on it. */
const FLOOR_MS = 460;
/* Must not be shorter than `.boot.is-leaving`'s own animation, or the mark is
   removed from the page part-way through fading and the last of it cuts. */
const FADE_MS = 820;

function gentle(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Which of the three states the veil is in, given whether the app knows what to
 * show yet.
 *
 * `held` covers the server render and everything up to `ready`, `leaving` is the
 * dissolve, and `gone` unmounts it. The app underneath is mounted at `leaving`
 * rather than at `gone`, which is what makes this a dissolve and not a cut —
 * both are on screen for the length of the fade.
 */
export function useBootPhase(ready: boolean): BootPhase {
  const [phase, setPhase] = useState<BootPhase>('held');
  // When the veil went up. A ref rather than state because nothing renders
  // differently for it, and null until mount so the server never reads a clock.
  const since = useRef<number | null>(null);

  useEffect(() => {
    if (since.current === null) since.current = performance.now();
  }, []);

  useEffect(() => {
    if (phase !== 'held' || !ready) return;
    const soft = gentle();
    const floor = soft ? 0 : FLOOR_MS;
    const waited = performance.now() - (since.current ?? performance.now());
    const t = setTimeout(() => setPhase('leaving'), Math.max(0, floor - waited));
    return () => clearTimeout(t);
  }, [ready, phase]);

  useEffect(() => {
    if (phase !== 'leaving') return;
    const t = setTimeout(() => setPhase('gone'), gentle() ? 0 : FADE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  return phase;
}

/**
 * The Fian mark: the logo's silhouette, filled from the tokens, with a light
 * travelling across it.
 *
 * There is no <img> here and the first pass had one — worth writing down,
 * because the reason only showed up in a screenshot. The artwork is white, and
 * on the light scheme the field it stands on is near-white, so the mark was
 * very nearly invisible: recognisable in the source, gone on the screen. A
 * masked element takes only the shape from the file and its colour from
 * `--chrome-ink`, which already flips mint-on-dark to green-on-pale for every
 * line and wash in the app — so the mark now flips with it for free, and the
 * one thing that cannot flip is the shape, which is the one thing that should
 * not.
 *
 * `shine` is off by default: at the size the gate uses it the sweep would be a
 * twitch rather than a shine.
 */
export function Mark({ shine = false }: { shine?: boolean }) {
  return (
    <span className={`mark${shine ? ' is-shining' : ''}`} aria-hidden="true">
      {shine && <span className="mark-sheen" />}
    </span>
  );
}

export function Boot({ leaving = false }: { leaving?: boolean }) {
  // Silence is honest for about two seconds. After that, a mark sitting still
  // is indistinguishable from a mark that has stopped, and the load effect in
  // ./console retries for a good while longer than that before it says anything.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 2400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`boot${leaving ? ' is-leaving' : ''}`}
      // Not a live region and not a dialog: it is the whole page while it is up.
      // `busy` is what a screen reader can usefully do with that.
      aria-busy={!leaving}
      role="status"
      aria-label="Loading"
    >
      <Mark shine />
      <p className={`boot-note${slow ? ' is-shown' : ''}`}>Still connecting…</p>
    </div>
  );
}
